import Spline from '@splinetool/react-spline';
import Image from 'next/image';
import { FC, PropsWithChildren, useRef } from 'react';
import CTA from 'react-scale/components/elements/cta';
import MeshGradient from 'react-scale/components/elements/webgl/mesh-gradient';
import Logo from 'react-scale/components/website/Logo';
import type { HeroWithImage } from './image-asset';
import type { HeroWithVideo } from './video-asset';
import type { HeroWithSpline } from './spline-asset';
import type { HeroWithContent } from './content-asset';
import ImageAsset from './image-asset';
import VideoAsset from './video-asset';
import SplineAsset from './spline-asset';
import ContentAsset from './content-asset';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { SingleFieldHSForm } from 'react-scale/components/elements/hubspot/single-field-form';

export type CTA = Pick<
  { theme: any; cta: any; themeColors: any; center: any; size: any },
  'themeColors' | 'size'
>;

type ProductIconAsIcon = {
  scene?: never;
  src: string;
  alt: string;
  width: number;
  height: number;
  widthMobile: number;
  heightMobile: number;
};

type ProductIconAsSplineModel = {
  scene: string;
  src?: never;
  alt?: never;
  height: number;
  width: number;
  widthMobile?: never;
  heightMobile?: never;
};

type Location = {
  name?: string;
  date?: string;
};

export type HeroDefaultProps = {
  scaleLogo?: boolean;
  location?: Location;
  title: string;
  product: string;
  subtitle: string;
  cta?: CTA;
  logo?: ProductIconAsSplineModel | ProductIconAsIcon;
  webgl?: boolean;
  useHubSpotCTA?: boolean;
  formId?: string;
};

type CentralizedHero = HeroWithImage | HeroWithVideo | HeroWithSpline | HeroWithContent;

const heroIsImage = (hero: CentralizedHero): hero is HeroWithImage => hero.image !== undefined;
const heroIsVideo = (hero: CentralizedHero): hero is HeroWithVideo => hero.video !== undefined;
const heroIsSpline = (hero: CentralizedHero): hero is HeroWithSpline => hero.scene !== undefined;

const logoIsIcon = (
  logo: ProductIconAsSplineModel | ProductIconAsIcon
): logo is ProductIconAsIcon => logo.src !== undefined;
const logoIsSplineModel = (
  logo: ProductIconAsSplineModel | ProductIconAsIcon
): logo is ProductIconAsSplineModel => logo.scene !== undefined;

const CentralizedHero: FC<PropsWithChildren<CentralizedHero>> = props => {
  return (
    <>
      {props.children}

      <div className="relative overflow-hidden">
        {props.webgl && (
          <div className="absolute z-0 inset-0 overflow-hidden max-w-[100vw]">
            <div className="absolute inset-0 w-full bg-gradient-to-b from-black" />
            <div className="absolute inset-0 w-full bg-gradient-to-b from-black" />
            <MeshGradient
              style={{
                '--gradient-color-1': '#D1AAD7',
                '--gradient-color-2': '#5933b2',
                '--gradient-color-3': '#8349FF',
                '--gradient-color-4': '#7B8CE7'
              }}
            />
          </div>
        )}

        <div className="container relative grid items-center gap-8 lg:gap-14 lg:grid-cols-2 py-18 md:py-20 lg:py-28 xl:py-36">
          <div>
            <div className="flex flex-col items-center justify-center mb-12 lg:justify-start lg:flex-row lg:mb-4">
              {props.logo &&
                (() => {
                  if (logoIsIcon(props.logo)) {
                    return (
                      <Icon
                        src={props.logo.src}
                        alt={props.logo.alt}
                        width={props.logo.width}
                        height={props.logo.height}
                        widthMobile={props.logo.widthMobile}
                        heightMobile={props.logo.heightMobile}
                      />
                    );
                  } else if (logoIsSplineModel(props.logo)) {
                    return (
                      <SplineIcon
                        scene={props.logo.scene}
                        width={props.logo.width}
                        height={props.logo.height}
                      />
                    );
                  }
                })()}
              <div className="flex flex-col items-center justify-center lg:block">
                {props.scaleLogo && (
                  <>
                    <div className="mb-4 lg:hidden">
                      <Logo width="68px" fillmobile="#A3A3A3" />
                    </div>
                    <div className="hidden mb-2 lg:block">
                      <Logo width="82px" fill="#A3A3A3" />
                    </div>
                  </>
                )}

                {props.location && (
                  <h5 className="flex items-center mb-8 space-x-4 text-sm">
                    {props.location.name && (
                      <div className="flex items-center space-x-2">
                        <svg width="18" height="19" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g
                            clipPath="url(#a)"
                            stroke="#fff"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M15.75 8c0 5.25-6.75 9.75-6.75 9.75S2.25 13.25 2.25 8a6.75 6.75 0 0 1 13.5 0Z" />
                            <path d="M9 10.25a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5Z" />
                          </g>
                          <defs>
                            <clipPath id="a">
                              <path fill="#fff" d="M0 .5h18v18H0z" />
                            </clipPath>
                          </defs>
                        </svg>
                        <span>{props.location.name}</span>
                      </div>
                    )}
                    {props.location.name && props.location.date && (
                      <span className="bg-white h-4 w-[1px]" />
                    )}
                    {props.location.date && (
                      <div className="flex items-center space-x-2">
                        <svg width="18" height="19" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M14.25 3.5H3.75A1.5 1.5 0 0 0 2.25 5v10.5a1.5 1.5 0 0 0 1.5 1.5h10.5a1.5 1.5 0 0 0 1.5-1.5V5a1.5 1.5 0 0 0-1.5-1.5ZM12 2v3M6 2v3M2.25 8h13.5"
                            stroke="#fff"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>{props.location.date}</span>
                      </div>
                    )}
                  </h5>
                )}

                <h1 className="font-aeonik text-center lg:text-left text-4xl md:text-5.5xl lg:text-6xl">
                  <ReactMarkdown rehypePlugins={[rehypeRaw]}>{props.title}</ReactMarkdown>{' '}
                  <span className="text-gradient">{props.product}</span>
                </h1>
              </div>
            </div>

            <ReactMarkdown className="text-lg text-center sm:px-0 lg:text-left lg:text-2xl lg:font-aeonik lg:mb-8 sm:w-3/4 sm:mx-auto lg:mx-0 lg:w-full">
              {props.subtitle}
            </ReactMarkdown>

            <div className="hidden w-full mx-auto lg:flex gap-x-8 sm:max-w-max lg:max-w-none">
              {(props.useHubSpotCTA ?? true) && props.cta ? (
                // @ts-ignore: Unreachable code error
                <CTA cta={props.cta} />
              ) : (
                <SingleFieldHSForm formInstanceId="centralized-hero-mobile" id={props.formId} />
              )}
            </div>
          </div>

          {(() => {
            if (heroIsImage(props)) {
              return <ImageAsset image={props.image} />;
            } else if (heroIsVideo(props)) {
              return <VideoAsset {...props.video} />;
            } else if (heroIsSpline(props)) {
              return <SplineAsset scene={props.scene} mobileImage={props.mobileImage} />;
            } else {
              return <ContentAsset content={props.content} />;
            }
          })()}

          <div className="lg:hidden">
            {(props.useHubSpotCTA ?? true) && props.cta ? (
              //  @ts-ignore: Unreachable code error
              <CTA cta={props.cta} />
            ) : (
              <SingleFieldHSForm formInstanceId="centralized-hero" id={props.formId} />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const Icon: FC<ProductIconAsIcon> = ({ src, alt, width, height, widthMobile, heightMobile }) => (
  <div className="mb-6 lg:mb-0 lg:mr-6">
    <span className="lg:hidden">
      <Image
        src={src}
        alt={alt}
        width={widthMobile ?? '70'}
        height={heightMobile ?? '81'}
        layout="fixed"
      />
    </span>
    <span className="hidden select-none lg:flex">
      <Image src={src} width={width} height={height} alt={alt} layout="fixed" />
    </span>
  </div>
);
const SplineIcon: FC<ProductIconAsSplineModel> = ({ scene, width, height }) => {
  const splineIconRef = useRef<HTMLCanvasElement>(null);

  return (
    <div className="mb-6 lg:mb-0 lg:mr-6" style={{ width: width, height: height }}>
      <Spline
        ref={splineIconRef}
        scene={scene}
        onLoad={() => {
          splineIconRef.current.style.width = `${width}px`;
          splineIconRef.current.style.height = `${height}px`;
        }}
      />
    </div>
  );
};

export default CentralizedHero;
